import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Progress } from "@/shared/ui/progress";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { ArrowLeft, CheckCircle } from "lucide-react";
import Papa from "papaparse";
import { supabase } from "@/shared/integrations/supabase/client";
import { requireUserIdForDatabase } from "@/shared/lib/userIdUtils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/hooks/useAuth";
import { FeatureLockOverlay } from "@/shared/components/FeatureLockOverlay";

import { UploadStep } from "./assetBulkImport/UploadStep";
import { MappingStep } from "./assetBulkImport/MappingStep";
import {
  getRequiredFields,
  normalizeCondition,
  OPTIONAL_FIELDS,
  type ImportMode,
} from "./assetBulkImport/fields";
import {
  validateAssetRows,
  type ImportError,
  type ParsedAsset,
} from "./assetBulkImport/validation";

interface Property {
  id: string;
  name: string;
  address: string;
}

interface AssetBulkImportProps {
  onComplete?: () => void;
  /** When provided, single-property mode is forced and the selector is locked. */
  propertyId?: string;
}

export default function AssetBulkImport({ onComplete, propertyId }: AssetBulkImportProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedAsset[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importComplete, setImportComplete] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string>(propertyId ?? "");
  const [importMode, setImportMode] = useState<ImportMode>("single");
  const [accessiblePropertyIds, setAccessiblePropertyIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (propertyId) {
      setSelectedProperty(propertyId);
      setImportMode("single");
    }
  }, [propertyId]);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const { data, error } = await supabase
        .from("properties")
        .select("id, name, address")
        .order("name");

      if (error) throw error;
      setProperties(data || []);
      setAccessiblePropertyIds(new Set((data || []).map((p) => p.id)));
    } catch (error) {
      console.error("Error loading properties:", error);
      toast({
        title: "Error",
        description: "Failed to load properties",
        variant: "destructive",
      });
    }
  };

  const parseFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const data = results.data as ParsedAsset[];
        const filtered = data.filter((row) =>
          Object.values(row).some((value) => value && value.trim())
        );
        setParsedData(filtered);
        initializeColumnMapping(Object.keys(filtered[0] || {}));
        setCurrentStep(2);
      },
      error: (error) => {
        toast({
          title: "Parse error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const initializeColumnMapping = (csvColumns: string[]) => {
    const mapping: Record<string, string> = {};
    const allFields = [...getRequiredFields(importMode), ...OPTIONAL_FIELDS];

    allFields.forEach((field) => {
      const matchingColumn = csvColumns.find(
        (col) => col.toLowerCase().replace(/[_\s]/g, "") === field.key.toLowerCase().replace(/[_\s]/g, "")
      );
      if (matchingColumn) {
        mapping[field.key] = matchingColumn;
      }
    });

    setColumnMapping(mapping);
  };

  const handleFileSelected = (file: File) => {
    setFile(file);
    parseFile(file);
  };

  const validateData = () => {
    if (importMode === "single" && !selectedProperty) {
      toast({
        title: "Validation Failed",
        description: "Please select a property for the assets",
        variant: "destructive",
      });
      return false;
    }

    const newErrors = validateAssetRows({
      parsedData,
      columnMapping,
      importMode,
      accessiblePropertyIds,
    });
    setErrors(newErrors);

    if (newErrors.length === 0) {
      toast({
        title: "✅ Validation Passed",
        description: `All ${parsedData.length} rows are valid and ready to import`,
      });
    } else {
      toast({
        title: "❌ Validation Failed",
        description: `Found ${newErrors.length} errors in ${parsedData.length} rows. Check details below.`,
        variant: "destructive",
      });
    }

    return newErrors.length === 0;
  };

  const handleImport = async () => {
    if (!validateData()) return;
    if ((importMode === "single" && !selectedProperty) || !user) return;

    setImporting(true);
    setProgress(0);

    try {
      let successfulImports = 0;
      const failedImports: { row: number; error: string; data: any }[] = [];
      const total = parsedData.length;

      for (let i = 0; i < parsedData.length; i++) {
        const row = parsedData[i];

        const assetData: any = {
          property_id: importMode === "single"
            ? selectedProperty
            : (columnMapping["property_id"] ? row[columnMapping["property_id"]]?.trim() : ""),
          created_by: requireUserIdForDatabase(),
        };

        [...getRequiredFields(importMode), ...OPTIONAL_FIELDS].forEach((field) => {
          const columnName = columnMapping[field.key];
          const value = columnName ? row[columnName]?.trim() : "";

          if (value) {
            if (["purchase_price", "size_tons", "health_score", "remaining_useful_life_months"].includes(field.key)) {
              assetData[field.key] = parseFloat(value);
            } else if (field.key === "condition") {
              assetData[field.key] = normalizeCondition(value);
            } else if (field.key === "gas_pack" || field.key === "separate_air_handler") {
              assetData[field.key] = value.toUpperCase() === "TRUE";
            } else {
              assetData[field.key] = value;
            }
          }
        });

        if (!assetData.health_score) assetData.health_score = 85;
        if (!assetData.lifecycle_stage) assetData.lifecycle_stage = "operation";

        try {
          const { error } = await supabase
            .from("property_assets")
            .insert(assetData)
            .select("*");

          if (error) {
            failedImports.push({
              row: i + 1,
              error: `Database error: ${error.message} (Code: ${error.code})`,
              data: assetData,
            });
          } else {
            successfulImports++;
          }
        } catch (error: any) {
          failedImports.push({
            row: i + 1,
            error: `Unexpected error: ${error.message || String(error)}`,
            data: assetData,
          });
        }

        setProgress(((i + 1) / total) * 100);
      }

      setSuccessCount(successfulImports);
      setImportComplete(true);
      setCurrentStep(3);

      if (failedImports.length === 0) {
        toast({
          title: "🎉 Import Success!",
          description: `Successfully imported all ${successfulImports} assets`,
        });
      } else if (successfulImports > 0) {
        toast({
          title: "⚠️ Partial Import",
          description: `Imported ${successfulImports} of ${total} assets. ${failedImports.length} failed - check console for details.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "❌ Import Failed",
          description: `No assets were imported. All ${total} rows failed - check console for details.`,
          variant: "destructive",
        });
      }

      if (failedImports.length > 0) {
        console.group("❌ FAILED IMPORTS - DETAILED BREAKDOWN:");
        failedImports.forEach((failure) => {
          console.log(`Row ${failure.row}: ${failure.error}`);
          console.log("Data:", failure.data);
        });
        console.groupEnd();
      }
    } catch (error) {
      console.error("💥 Critical import error:", error);
      toast({
        title: "💥 Critical Error",
        description: `Import process failed: ${error instanceof Error ? error.message : String(error)}`,
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setParsedData([]);
    setColumnMapping({});
    setErrors([]);
    setImporting(false);
    setProgress(0);
    setImportComplete(false);
    setSuccessCount(0);
    setCurrentStep(1);
    setSelectedProperty("");
    setImportMode("single");
  };

  return (
    <div className="relative container mx-auto p-6 max-w-6xl">
      <FeatureLockOverlay
        title="Asset Import Locked"
        message="Bulk asset import is currently unavailable. Contact a Robotic Imaging representative for assistance."
      />

      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Import Assets</h1>
            <p className="text-muted-foreground">Upload a CSV file to bulk import assets</p>
          </div>
        </div>
      </div>

      <Tabs value={`step-${currentStep}`} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="step-1" disabled={currentStep < 1}>Upload File</TabsTrigger>
          <TabsTrigger value="step-2" disabled={currentStep < 2}>Review & Map</TabsTrigger>
          <TabsTrigger value="step-3" disabled={currentStep < 3}>Import Complete</TabsTrigger>
        </TabsList>

        <TabsContent value="step-1" className="space-y-4">
          <UploadStep
            propertyId={propertyId}
            properties={properties}
            importMode={importMode}
            setImportMode={setImportMode}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            file={file}
            parsedDataLength={parsedData.length}
            onFileSelected={handleFileSelected}
          />
        </TabsContent>

        <TabsContent value="step-2" className="space-y-4">
          <MappingStep
            propertyId={propertyId}
            properties={properties}
            importMode={importMode}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            file={file}
            parsedData={parsedData}
            columnMapping={columnMapping}
            setColumnMapping={setColumnMapping}
            errors={errors}
            importing={importing}
            onValidate={validateData}
            onImport={handleImport}
          />
        </TabsContent>

        <TabsContent value="step-3" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-success" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {importing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Importing assets...</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {importComplete && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Successfully imported {successCount} of {parsedData.length} assets.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button onClick={() => navigate("/assets")}>
                  View Assets
                </Button>
                <Button onClick={resetImport} variant="outline">
                  Import More
                </Button>
                {onComplete && (
                  <Button onClick={onComplete} variant="outline">
                    Close
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
